/**
 * Le catalogue des erreurs nommées, telles que le professeur les lit.
 *
 * Le chemin d'un code : le gen range ses erreurs dans `contenu.erreurs` (le code
 * et la réponse fausse qu'il produit), `trouverErreurs` (questionUtils) reconnaît
 * celle de l'élève et le code part avec la réponse ; ici, il devient un libellé.
 * Le même mot sert aux trois endroits : le harnais vérifie que tout code posé par
 * un gen existe ici, et `trouverErreurs` n'envoie que des codes d'ici.
 *
 * Catalogue validé le 03/10/2026 : codes communs regroupés par un validateur externe
 * (programme du cycle 4, sources Eduscol, IREM, APMEP, DEPP), libellés réécrits
 * pour être lus en deux secondes, passés à une épreuve de lecture.
 * Depuis le 04/10/2026, ce fichier est la source unique, éditée à la main (il n'est
 * plus régénéré depuis l'atelier). Le mécanisme complet : docs/SFD_CODES_ERREUR.md.
 */

/**
 * code -> { libelle, sousTitre?, regle?, description, notion }
 * libelle : l'erreur, ce que fait l'élève (titre de la carte) ; sousTitre : la formule
 * qui la montre, quand le libellé est court ; regle : la règle de l'attendu à reprendre,
 * écrite telle qu'elle s'enseigne, sans « Ne sait pas » (la carte l'affiche sous
 * « À reprendre ») : lue sur les réponses d'une classe, ou écrite par un juge programme-maths
 * et relue par un second (passe du 04/10/2026) ; tout code en service en a une, et deux codes
 * d'un même attendu qui demandent la même règle portent le même texte, mot pour mot (la carte
 * les regroupe) ; notion : le chapitre de l'erreur.
 */
export const ERREURS = {
    abscisse_ordonnee_echangees: {
        libelle: 'Échange abscisse et ordonnée : l\'abscisse lue sur l\'axe vertical',
        regle: 'L\'abscisse se lit sur l\'axe horizontal et s\'écrit en premier',
        description: 'L\'élève ne sait pas quel axe porte l\'abscisse et lequel porte l\'ordonnée, ni que l\'ordre des deux nombres du couple compte.',
        notion: 'repérage',
    },
    addition_pour_produit: {
        libelle: 'Additionne au lieu de multiplier : aire du rectangle = L + l',
        regle: 'L\'aire et le volume se calculent par des multiplications, pas des additions : rectangle $A = L \\times l$, pavé $V = L \\times l \\times h$',
        description: 'L\'élève additionne des nombres à multiplier : les dimensions d\'une aire ou d\'un volume, ou la moyenne et le nombre de valeurs pour le total.',
        notion: 'aires',
    },
    aire_de_base_pour_volume: {
        libelle: 'Donne une aire pour le volume : $V = L \\times l$',
        regle: 'Volume d\'un pavé, d\'un prisme, d\'un cylindre : aire de la base $\\times$ hauteur ; pavé $V = L \\times l \\times h$, cylindre $V = \\pi \\times r \\times r \\times h$',
        description: 'L\'élève s\'arrête à l\'aire de la base, ou donne l\'aire des faces du pavé, à la place du volume : V = L × l, 12 cm³ pour un prisme.',
        notion: 'volumes',
    },
    aire_pour_volume: {
        libelle: 'Calcule une aire pour le volume du cube : 3 × 3 ou 6 × 3 × 3 cm³',
        regle: 'Le volume d\'un cube, c\'est arête $\\times$ arête $\\times$ arête : $V = c \\times c \\times c$ ; pour une arête de 5 cm, $5 \\times 5 \\times 5 = 125$ cm³',
        description: 'L\'élève calcule l\'aire d\'une face ou de toutes les faces du cube à la place de son volume.',
        notion: 'volumes',
    },
    ajout_zero: {
        libelle: 'Ajoute un zéro au décimal : 1,42 × 10 = 1,420',
        regle: 'Multiplier par 10, 100, 1 000 décale chaque chiffre de 1, 2, 3 rangs vers la gauche, diviser vers la droite : $263 \\div 10 = 26{,}3$ (1 rang)',
        description: 'L\'élève applique aux décimaux la règle des entiers « fois 10, on ajoute un zéro », sans voir que ce zéro ne change pas la valeur.',
        notion: 'nombres décimaux',
    },
    autre_axe_de_la_figure: {
        libelle: 'Prend un autre axe de la figure : l\'image par (BD) au lieu de (AC)',
        regle: 'Symétrie axiale : pliage le long de l\'axe demandé, repéré sur la figure ; ses points restent en place, les autres passent de l\'autre côté, à la même distance',
        description: 'L\'élève fait bien une symétrie axiale, mais par rapport à un autre axe de la figure que celui qui est demandé.',
        notion: 'transformations',
    },
    axe_ou_centre_dans_la_figure: {
        libelle: 'Croit que l\'axe ou le centre est toujours sur la figure',
        regle: 'L\'axe d\'une symétrie axiale peut être hors de la figure',
        description: 'L\'élève pense que l\'axe ou le centre d\'une symétrie fait partie de la figure, alors que l\'un comme l\'autre peut être en dehors.',
        notion: 'transformations',
    },
    axe_ou_centre_decale: {
        libelle: 'Décale l\'axe ou le centre : une parallèle à l\'axe prise pour l\'axe',
        regle: 'Pour trouver l\'axe ou le centre, on joint un point à son image : l\'axe est la médiatrice de ce segment, le centre est son milieu',
        description: 'L\'élève choisit une parallèle au bon axe ou un point voisin du centre, sans vérifier qu\'un point et son image sont à égale distance de l\'axe ou du centre.',
        notion: 'transformations',
    },
    bas_additionnes: {
        libelle: 'Calcule numérateurs et dénominateurs à part : 1/6 + 3/4 = 4/10',
        regle: 'Pour additionner ou soustraire des fractions : même dénominateur d\'abord, puis on ajoute ou soustrait les numérateurs et on garde ce dénominateur',
        description: 'L\'élève traite chaque fraction comme deux entiers séparés et calcule les numérateurs entre eux, puis les dénominateurs.',
        notion: 'fractions',
    },
    base_fois_exposant: {
        libelle: 'Multiplie par l\'exposant',
        sousTitre: '$a^n$ lu $a \\times n$ : $4^3 = 12$',
        regle: '$a^n$ est le produit de $n$ facteurs égaux à $a$ : $4^3 = 4 \\times 4 \\times 4 = 64$',
        description: 'L\'élève multiplie la base par l\'exposant au lieu de multiplier la base par elle-même autant de fois que l\'exposant l\'indique.',
        notion: 'puissances',
    },
    base_plus_exposant: {
        libelle: 'Ajoute l\'exposant à la base : 11² = 13',
        regle: '$a^n$ est le produit de $n$ facteurs égaux à $a$ : $4^3 = 4 \\times 4 \\times 4 = 64$',
        description: 'L\'élève combine la base et l\'exposant par une addition, sans voir que l\'exposant compte des facteurs égaux.',
        notion: 'puissances',
    },
    bases_par_position: {
        libelle: 'Repère les bases par leur place : « bases devant et derrière »',
        regle: 'Les bases d\'un prisme droit sont ses deux faces superposables et parallèles, où qu\'elles soient sur le dessin ; les autres faces sont des rectangles',
        description: 'L\'élève prend pour bases les faces posées en bas ou vues de face, au lieu des deux faces superposables du prisme.',
        notion: 'solides',
    },
    carre_cote_fois_2: {
        libelle: 'Multiplie le côté du carré par 2 : $A = c \\times 2$',
        regle: 'L\'aire d\'un carré, c\'est côté $\\times$ côté : $A = c \\times c$ ; pour un côté de 5 m, $5 \\times 5 = 25$ m²',
        description: 'L\'élève multiplie le côté par 2 au lieu de le multiplier par lui-même : 22 cm² pour un carré de côté 11 cm.',
        notion: 'aires',
    },
    carre_dans_x_plus_x: {
        libelle: 'Prend $x + x$ pour $x \\times x$ : $x + x = x^2$',
        regle: '$x + x = 2x$ mais $x \\times x = x^2$, le carré vient du produit',
        description: 'L\'élève lit x + x comme x × x : x + x = x².',
        notion: 'calcul littéral',
    },
    carre_de_trop: {
        libelle: 'Met un carré dans une somme : 3x + 7x = 10x²',
        regle: '$3x + 2x = 5x$ et $3x \\times 2x = 6x^2$, nombres devant $x$ compris',
        description: 'L\'élève additionne bien les nombres mais croit que des lettres ajoutées donnent un carré, comme dans un produit.',
        notion: 'calcul littéral',
    },
    carre_ecrit_en_double: {
        libelle: 'Confond le carré et le double : le carré de $t$, c\'est $2t$',
        regle: 'Le carré de $x$, c\'est $x$ multiplié par lui-même : $x \\times x = x^2$ ; le double de $x$, c\'est $2x$',
        description: 'L\'élève écrit le carré d\'un nombre comme son double : 2x au lieu de x².',
        notion: 'calcul littéral',
    },
    carre_lu_comme_double: {
        libelle: 'Prend $x \\times x$ pour $x + x$ : $x \\times x = 2x$',
        regle: '$x + x = 2x$ mais $x \\times x = x^2$, le carré vient du produit',
        description: 'L\'élève prend le produit d\'un nombre par lui-même pour son double : x × x = 2x.',
        notion: 'calcul littéral',
    },
    carre_voisin: {
        libelle: 'Confond deux carrés voisins : 64 serait le carré de 7 ou de 9',
        regle: 'Le carré d\'un nombre, c\'est ce nombre multiplié par lui-même ; connaitre les carrés parfaits de 0 à 12 : $64 = 8 \\times 8 = 8^2$, $144 = 12^2$',
        description: 'L\'élève connaît à peu près les carrés parfaits et prend un entier voisin : 7 ou 9 pour le nombre dont 64 est le carré.',
        notion: 'puissances',
    },
    cas_particulier_generalise: {
        libelle: 'Prête au parallélogramme les diagonales égales du rectangle',
        regle: 'Les diagonales d\'un parallélogramme se coupent en leur milieu ; si elles ont aussi la même longueur, c\'est un rectangle',
        description: 'L\'élève prête à toute une famille une propriété vraie seulement pour un de ses cas particuliers, comme si la figure type était la seule.',
        notion: 'parallélogrammes',
    },
    centre_sans_image: {
        libelle: 'Oublie que le centre et l\'axe ne bougent pas : « O n\'a pas d\'image »',
        regle: 'Un point de l\'axe, ou le centre $O$, est sa propre image',
        description: 'L\'élève ne sait pas qu\'un point qui ne bouge pas, le centre d\'un demi-tour ou un point de l\'axe, est sa propre image.',
        notion: 'transformations',
    },
    coefficient_implicite_oublie: {
        libelle: 'Oublie le 1 devant la lettre seule : 6n + n = 6n',
        description: 'L\'élève ne voit pas le 1 caché devant une lettre seule et la compte pour zéro dans la somme.',
        notion: 'calcul littéral',
    },
    coefficient_lu_comme_exposant: {
        libelle: 'Prend le nombre devant la lettre pour un exposant : $6 \\times n$ s\'écrit $n^6$',
        regle: '$3 \\times x$ s\'écrit $3x$, et $3x$ c\'est $3 \\times x$ ; c\'est $x \\times x \\times x$ qui s\'écrit $x^3$',
        description: 'L\'élève prend le nombre devant la lettre pour un exposant : 6 × n devient n⁶, 3a devient a × a × a.',
        notion: 'calcul littéral',
    },
    comparaison_oubliee: {
        libelle: 'Calcule sans comparer : pour x = 5, 5x + 7 = 12 ? trouve 32, dit oui',
        description: 'L\'élève croit que tester une égalité, c\'est calculer un membre, et répond sans comparer le résultat à l\'autre membre.',
        notion: 'calcul littéral',
    },
    complement: {
        libelle: 'Donne la part qui reste : 60 % de 120 = 48',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève calcule la part de ceux qui ne sont pas concernés, le complément à 100 %, au lieu de la part demandée.',
        notion: 'pourcentages',
    },
    complement_du_pourcentage: {
        libelle: 'Donne le pourcentage complémentaire : $10 = \\frac{98}{100} \\times 500$',
        regle: 'Dans $b = \\frac{\\ldots}{100} \\times c$, ce qui manque est le pourcentage de $c$ qui vaut $b$ : $30 = \\frac{3}{100} \\times 1\\,000$, car 1 % de 1 000, c\'est 10',
        description: 'L\'élève trouve la part qui reste au lieu du pourcentage cherché : 98 au lieu de 2 dans 10 = …/100 × 500.',
        notion: 'pourcentages',
    },
    comptage_inverse: {
        libelle: 'Compte la partie décimale vers la droite : −2,7 entre −2 et −1',
        regle: 'Repérer un décimal négatif : $-3{,}7$ est entre $-4$ et $-3$, à gauche de $-3$',
        description: 'L\'élève part du bon entier mais compte la partie décimale d\'un nombre négatif vers les positifs, comme pour un positif.',
        notion: 'nombres relatifs',
    },
    concatenation: {
        libelle: 'Colle les chiffres : pour $x = 2$, $3x = 32$',
        regle: 'En remplaçant $x$ par un nombre, on garde l\'opération : pour $x = 2$, $3x = 3 \\times 2$ et $x + 8 = 2 + 8$',
        description: 'L\'élève remplace la lettre par sa valeur en l\'écrivant à côté du nombre, comme un chiffre de plus, au lieu de multiplier.',
        notion: 'calcul littéral',
    },
    concours_nie: {
        libelle: 'Croit que les trois médianes ne se coupent pas en un même point',
        description: 'L\'élève ignore que les trois médianes d\'un triangle se coupent toujours en un même point, comme les hauteurs.',
        notion: 'droites remarquables',
    },
    conservation_niee: {
        libelle: 'Croit qu\'une symétrie change les angles ou les longueurs',
        regle: 'La symétrie axiale et le demi-tour conservent les longueurs et les angles : l\'image d\'un segment a la même longueur, celle d\'un triangle les mêmes angles',
        description: 'L\'élève ne sait pas qu\'une symétrie déplace la figure sans la déformer : l\'image garde les mêmes longueurs et les mêmes angles.',
        notion: 'transformations',
    },
    contour_pris_pour_base: {
        libelle: 'Prend le contour du dessin pour sa base : un hexagone pour un pavé',
        regle: 'La base d\'un prisme droit est l\'une de ses deux faces superposables, pas le contour du dessin en perspective, qui mélange plusieurs faces',
        description: 'L\'élève lit la forme de la base sur la silhouette du solide dessiné en perspective, sans repérer les deux faces superposables qui forment les bases.',
        notion: 'solides',
    },
    cote_pris_pour_hauteur: {
        libelle: 'Prend le côté oblique pour la hauteur : aire = base × côté ÷ 2',
        regle: 'La hauteur d\'un triangle est perpendiculaire à la base et passe par le sommet opposé : un côté ne sert de hauteur que s\'il est perpendiculaire à la base',
        description: 'L\'élève prend un côté du triangle pour sa hauteur, sans voir que la hauteur est perpendiculaire à la base.',
        notion: 'aires',
    },
    cube_arete_fois_3: {
        libelle: 'Multiplie l\'arête du cube par 3 : $V = c \\times 3$',
        regle: 'Le volume d\'un cube, c\'est arête $\\times$ arête $\\times$ arête : $V = c \\times c \\times c$ ; pour une arête de 5 cm, $5 \\times 5 \\times 5 = 125$ cm³',
        description: 'L\'élève multiplie l\'arête par 3 au lieu de la multiplier trois fois par elle-même : 15 cm³ pour un cube d\'arête 5 cm.',
        notion: 'volumes',
    },
    definitions_des_droites_confondues: {
        libelle: 'Confond les définitions : une médiane toujours perpendiculaire au côté opposé',
        regle: 'D\'après le codage : sommet et angle droit, hauteur ; sommet et milieu du côté opposé, médiane ; milieu et angle droit, médiatrice ; angles égaux, bissectrice',
        description: 'L\'élève prête à une droite remarquable la définition d\'une autre : la médiane perpendiculaire, la bissectrice par le milieu.',
        notion: 'droites remarquables',
    },
    demi_tour_pour_axiale: {
        libelle: 'Confond demi-tour et symétrie axiale : « un demi-tour est un pliage »',
        regle: 'Distinguer la symétrie axiale (pliage le long d\'un axe) et le demi-tour (autour d\'un centre)',
        description: 'L\'élève ne distingue pas le demi-tour, qui met la figure tête en bas, de la symétrie axiale, qui la retourne comme dans un miroir.',
        notion: 'transformations',
    },
    demi_tour_pour_translation: {
        libelle: 'Confond demi-tour et translation : « la figure a seulement glissé »',
        regle: 'Par un demi-tour, la figure est retournée tête en bas (tournée de 180° autour du centre) ; si elle a seulement glissé, c\'est une translation',
        description: 'L\'élève ne regarde pas si la figure est tête en bas ou à l\'endroit et prend un demi-tour pour un simple glissement, ou l\'inverse.',
        notion: 'transformations',
    },
    denominateur_grand: {
        libelle: 'Range les fractions comme leurs dénominateurs : 3/20 > 3/17',
        regle: 'À même numérateur, la plus grande a le plus petit dénominateur : $\\frac{3}{4} > \\frac{3}{7}$, un quart est plus grand qu\'un septième',
        description: 'L\'élève pense que plus de parts fait une plus grande fraction, sans voir que chaque part devient plus petite.',
        notion: 'fractions',
    },
    depart_pris_pour_image: {
        libelle: 'Redonne l\'objet de départ : l\'image de A par le demi-tour serait A',
        regle: 'Un point hors de l\'axe, ou autre que le centre, a pour image un autre point ; l\'image du segment $[AB]$ est $[A\'B\']$, celle du triangle $ABC$ est $A\'B\'C\'$',
        description: 'L\'élève donne pour image le point, le segment ou le triangle de départ lui-même, comme si la symétrie le laissait en place.',
        notion: 'transformations',
    },
    diametre_pour_rayon: {
        libelle: 'Prend le diamètre pour le rayon : aire du disque = π × d × d',
        regle: 'L\'aire du disque est $\\pi \\times$ rayon $\\times$ rayon : avec un diamètre, on le divise d\'abord par 2',
        description: 'L\'élève calcule l\'aire du disque avec le diamètre à la place du rayon, ce qui donne quatre fois l\'aire.',
        notion: 'aires',
    },
    distances_ajoutees: {
        libelle: 'Compte les deux nombres comme négatifs : −5 + 7 = −12',
        regle: 'À signes contraires, on soustrait les distances à zéro et on garde le signe du plus loin de zéro : $2 + (-14) = -12$',
        description: 'L\'élève met un signe moins devant la somme des distances à zéro, sans regarder le signe de chaque nombre.',
        notion: 'nombres relatifs',
    },
    distances_ajoutees_soustraction: {
        libelle: 'Additionne les distances à zéro et met un signe moins : $4 - (-6) = -10$',
        regle: 'Soustraire un nombre, c\'est ajouter son opposé : $4 - (-6) = 4 + 6 = 10$ ; $11 - 12 = 11 + (-12) = -1$ ; $-8 - 4 = -8 + (-4) = -12$',
        description: 'L\'élève ajoute, dans une soustraction, les distances à zéro et met un signe moins : 4 − (−6) = −10, 11 − 12 = −23.',
        notion: 'nombres relatifs',
    },
    divise_par_2: {
        libelle: 'Divise toujours le total par 2 : moyenne de 3 valeurs = total ÷ 2',
        regle: 'On divise la somme des valeurs par le nombre de valeurs',
        description: 'L\'élève garde le « divisé par 2 » de la moyenne de deux nombres et l\'applique quel que soit le nombre de valeurs.',
        notion: 'statistiques',
    },
    divise_par_a: {
        libelle: 'Lit 25 % comme « diviser par 25 » : 25 % de 200 = 8',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève prend le nombre écrit devant % pour un diviseur, sans relier a % à a centièmes du nombre.',
        notion: 'pourcentages',
    },
    diviser_par_numerateur: {
        libelle: 'Échange numérateur et dénominateur : 27 × 2/3 = 27 ÷ 2 × 3',
        regle: '$27 \\times \\frac{2}{3} = 27 \\div 3 \\times 2$',
        description: 'L\'élève partage par le numérateur et multiplie par le dénominateur, en échangeant le nombre de parts prises et celui du partage.',
        notion: 'fractions',
    },
    double_ecrit_en_puissance: {
        libelle: 'Met 2 ou 3 en exposant au lieu de multiplier : le double de $t$, c\'est $t^2$',
        regle: 'Le double de $t$, c\'est 2 fois $t$ : $2 \\times t = 2t$ ; le triple, 3 fois $t$ : $3 \\times t = 3t$',
        description: 'L\'élève écrit le double ou le triple d\'un nombre avec un exposant : t² pour le double de t, x³ pour le triple de x.',
        notion: 'calcul littéral',
    },
    double_ecrit_en_somme: {
        libelle: 'Ajoute 2 ou 3 au lieu de multiplier : le double de $t$, c\'est $t + 2$',
        regle: 'Le double de $t$, c\'est 2 fois $t$ : $2 \\times t = 2t$ ; le triple, 3 fois $t$ : $3 \\times t = 3t$',
        description: 'L\'élève lit « fois » comme « plus » : t + 2 pour le double de t, x + 3 pour le triple de x.',
        notion: 'calcul littéral',
    },
    droite_remarquable_confondue: {
        libelle: 'Confond les droites remarquables : médiane prise pour hauteur',
        regle: 'D\'après le codage : sommet et angle droit, hauteur ; sommet et milieu du côté opposé, médiane ; milieu et angle droit, médiatrice ; angles égaux, bissectrice',
        description: 'L\'élève reconnaît une droite remarquable à son allure sur le dessin, sans lire ce que le codage indique.',
        notion: 'droites remarquables',
    },
    ecart_au_lieu_de_somme: {
        libelle: 'Soustrait les distances à zéro de deux négatifs : (−8) + (−15) = −7',
        regle: 'Pour ajouter deux nombres de même signe, on ajoute les distances à zéro et on garde ce signe : $(-8) + (-7) = -15$',
        description: 'L\'élève applique à deux nombres négatifs la règle des signes contraires et fait la différence de leurs distances à zéro au lieu de leur somme.',
        notion: 'nombres relatifs',
    },
    ecart_au_lieu_de_somme_soustraction: {
        libelle: 'Fait l\'écart au lieu de la somme : $-8 - 4 = -4$',
        regle: 'Soustraire un nombre, c\'est ajouter son opposé : $4 - (-6) = 4 + 6 = 10$ ; $11 - 12 = 11 + (-12) = -1$ ; $-8 - 4 = -8 + (-4) = -12$',
        description: 'L\'élève lit −8 − 4 comme l\'écart entre 8 et 4 au lieu d\'ajouter −8 et −4 : −8 − 4 = −4.',
        notion: 'nombres relatifs',
    },
    effectif_manquant_a_l_envers: {
        libelle: 'Soustrait dans le mauvais ordre : $(17 + 13) - 37$ au lieu de $37 - (17 + 13)$',
        regle: 'L\'effectif manquant, c\'est le total moins la somme des effectifs connus : $37 - (17 + 13) = 7$',
        description: 'L\'élève soustrait le total à la somme des effectifs connus, au lieu de soustraire cette somme au total.',
        notion: 'statistiques',
    },
    effectif_pour_frequence: {
        libelle: 'Prend l\'effectif pour la fréquence : 6 sur 20, c\'est 6 %',
        regle: 'La fréquence d\'une catégorie, c\'est son effectif divisé par l\'effectif total, toujours entre 0 et 1 : 2 sur 10 donne $\\dfrac{2}{10} = 0{,}2$, soit 20 %',
        description: 'L\'élève donne le nombre de personnes comme fréquence, sans le rapporter à l\'effectif total : la fréquence n\'est pas vue comme une part.',
        notion: 'statistiques',
    },
    entier_colle_au_numerateur: {
        libelle: 'Colle l\'entier au numérateur : 3 + 2/5 = 32/5',
        description: 'L\'élève écrit l\'entier devant le numérateur, comme un chiffre de plus, au lieu de le convertir en parts : 3 + 2/5 devient 32/5.',
        notion: 'fractions',
    },
    equation_produit_par_soustraction: {
        libelle: 'Soustrait au lieu de diviser : $8t = 48$ donne $t = 40$',
        regle: 'Dans $ax = c$, $x$ est multiplié par $a$, on divise : $x = c \\div a$, $8t = 48$ donne $t = 48 \\div 8 = 6$ ; dans $x + b = c$, on soustrait : $x = c - b$',
        description: 'L\'élève défait la multiplication par une soustraction : 8t = 48 donne t = 40, 4x = 16 donne 16 − 4.',
        notion: 'équations',
    },
    exposant_au_lieu_de_la_base: {
        libelle: 'Donne l\'exposant au lieu du nombre : 25 est le carré de 2',
        description: 'L\'élève répond avec le nombre que dit le mot « carré » ou « cube » au lieu de chercher le nombre élevé à cette puissance.',
        notion: 'puissances',
    },
    exposant_ignore: {
        libelle: 'Ne tient pas compte de l\'exposant : 3² = 3',
        regle: 'Le carré d\'un nombre, c\'est ce nombre multiplié par lui-même : $r^2 = r \\times r$ ; disque $\\pi \\times r \\times r$, cylindre $\\pi \\times r \\times r \\times h$',
        description: 'L\'élève recopie la base et ne donne aucun sens au petit nombre écrit en haut.',
        notion: 'puissances',
    },
    exposant_lu_comme_chiffre: {
        libelle: 'Lit l\'exposant comme un chiffre : 2³ = 23',
        regle: '$a^n$ est le produit de $n$ facteurs égaux à $a$ : $4^3 = 4 \\times 4 \\times 4 = 64$',
        description: 'L\'élève écrit l\'exposant à la suite de la base comme un chiffre de plus, au lieu d\'y lire le nombre de facteurs égaux à la base.',
        notion: 'puissances',
    },
    facteurs_echanges: {
        libelle: 'Multiplie le mauvais numérateur : 2/3 et 7/12 écrits 2/12 et 28/12',
        regle: 'Pour changer de dénominateur, on multiplie le numérateur et le dénominateur par le même nombre : $\\frac{2}{5} = \\frac{2 \\times 4}{5 \\times 4} = \\frac{8}{20}$',
        description: 'L\'élève trouve le dénominateur commun mais multiplie chaque numérateur par le nombre qui convient à l\'autre fraction.',
        notion: 'fractions',
    },
    fait_de_table_voisin: {
        libelle: 'Répond à côté dans la table : $36 = 6 \\times 7$ (c\'est $6 \\times 6$)',
        regle: 'Les tables de multiplication, par cœur et dans les deux sens : $6 \\times 9 = 54$, donc dans $54 = 6 \\times \\ldots$, il manque 9',
        description: 'L\'élève donne le résultat ou le facteur d\'un produit voisin dans la table : 36 = 6 × 7 au lieu de 6 × 6.',
        notion: 'multiplication',
    },
    fois_numerateur: {
        libelle: 'Multiplie par le numérateur seul : les 2/3 de 30 = 2 × 30',
        regle: 'Les $\\frac{5}{8}$ de 24, c\'est $\\frac{5}{8} \\times 24$',
        description: 'L\'élève multiplie par le numérateur et oublie le partage par le dénominateur, comme si la fraction était un entier.',
        notion: 'fractions',
    },
    fraction_ajoutee_au_nombre: {
        libelle: 'Ajoute ou retire la fraction au nombre : 5/8 de 24 = 24,625 ou 23,375',
        regle: 'Les $\\frac{5}{8}$ de 24, c\'est $\\frac{5}{8} \\times 24$',
        description: 'L\'élève lit « prendre les a/b de » comme ajouter ou enlever a/b au nombre, au lieu de multiplier le nombre par la fraction.',
        notion: 'fractions',
    },
    fraction_inchangee: {
        libelle: 'Recopie la fraction sans multiplier : 1/7 × 7 = 1/7',
        regle: '$\\frac{a}{b}$ est le nombre qui, multiplié par $b$, donne $a$',
        description: 'L\'élève ne sait pas que a/b est le nombre qui, multiplié par b, donne a, et laisse la fraction telle quelle.',
        notion: 'fractions',
    },
    fraction_sous_1: {
        libelle: 'Ne voit pas qu\'une fraction peut dépasser 1 : 18/10 < 1',
        description: 'L\'élève voit une fraction comme une part d\'une unité, donc toujours plus petite que 1, et range mal celle qui dépasse 1.',
        notion: 'fractions',
    },
    frequence_a_l_envers: {
        libelle: 'Divise dans le mauvais sens : l\'effectif total divisé par son effectif',
        regle: 'La fréquence d\'une catégorie, c\'est son effectif divisé par l\'effectif total, toujours entre 0 et 1 : 2 sur 10 donne $\\dfrac{2}{10} = 0{,}2$, soit 20 %',
        description: 'L\'élève divise l\'effectif total par l\'effectif de la catégorie : la moitié donne une fréquence de 2.',
        notion: 'statistiques',
    },
    frequence_complementaire: {
        libelle: 'Donne la fréquence des autres catégories : 80 % pour « Chien », 2 sur 10',
        regle: 'La fréquence d\'une catégorie, c\'est son effectif divisé par l\'effectif total, toujours entre 0 et 1 : 2 sur 10 donne $\\dfrac{2}{10} = 0{,}2$, soit 20 %',
        description: 'L\'élève calcule la fréquence de toutes les autres catégories au lieu de celle demandée.',
        notion: 'statistiques',
    },
    gauche_a_droite: {
        libelle: 'Calcule de gauche à droite : 2 + 3 × 4 = 20',
        regle: 'D\'abord les parenthèses, puis $\\times$ et $\\div$, puis $+$ et $-$',
        description: 'L\'élève enchaîne les opérations dans l\'ordre d\'écriture, sans faire passer × et ÷ avant + et −.',
        notion: 'priorités opératoires',
    },
    hauteurs_du_triangle_rectangle: {
        libelle: 'Place les trois hauteurs d\'un triangle rectangle à l\'intérieur',
        regle: 'Une hauteur passe par un sommet et est perpendiculaire au côté opposé ; dans un triangle rectangle, les deux côtés de l\'angle droit sont des hauteurs',
        description: 'L\'élève ne voit pas que, dans un triangle rectangle, deux hauteurs sont les côtés de l\'angle droit.',
        notion: 'droites remarquables',
    },
    hauts_et_bas_multiplies: {
        libelle: 'Multiplie les numérateurs et les dénominateurs : 3/7 + 3/7 = 9/49',
        regle: 'Pour additionner ou soustraire des fractions : même dénominateur d\'abord, puis on ajoute ou soustrait les numérateurs et on garde ce dénominateur',
        description: 'L\'élève applique à une somme de fractions la règle du produit : numérateurs multipliés entre eux, dénominateurs multipliés entre eux.',
        notion: 'fractions',
    },
    hypothese_manquante: {
        libelle: 'Conclut avec trop peu : « 2 côtés parallèles, donc parallélogramme »',
        regle: 'Pour conclure, il faut toute la propriété caractéristique : parallélogramme = des diagonales qui se coupent en leur milieu ; carré = rectangle et losange',
        description: 'L\'élève prend une propriété de la figure pour une preuve de sa nature, sans vérifier que cette propriété suffit à conclure.',
        notion: 'raisonnement géométrique',
    },
    inclusion_inversee: {
        libelle: 'Range les familles à l\'envers : « un rectangle est un carré »',
        regle: 'Un carré est un rectangle et un losange, qui sont des parallélogrammes ; l\'inverse n\'est pas toujours vrai, et un trapèze n\'est pas toujours un parallélogramme',
        description: 'L\'élève renverse le lien entre deux familles de quadrilatères et prend la famille la plus large pour un cas de la plus petite.',
        notion: 'parallélogrammes',
    },
    inversion: {
        libelle: 'Prend les nombres dans le mauvais ordre : 3x = 12 donne x = 3/12',
        regle: 'Dans $ax = c$, $x$ est multiplié par $a$, on divise : $x = c \\div a$, $8t = 48$ donne $t = 48 \\div 8 = 6$ ; dans $x + b = c$, on soustrait : $x = c - b$',
        description: 'L\'élève ne voit pas que l\'ordre compte dans un quotient ou une différence et part du mauvais nombre.',
        notion: 'équations',
    },
    lecture_additive: {
        libelle: 'Remplace « fois » par « plus »',
        sousTitre: '$6 \\times n$ écrit $6 + n$',
        regle: '$3x$ c\'est $3 \\times x$, donc $x + x + x$',
        description: 'L\'élève lit le nombre collé à la lettre comme une addition : 3x devient 3 + x.',
        notion: 'calcul littéral',
    },
    lettre_devant_le_nombre: {
        libelle: 'Écrit la lettre devant le nombre : 3 × x s\'écrit x3',
        regle: 'Le produit d\'un nombre et d\'une lettre s\'écrit sans le signe $\\times$, le nombre devant la lettre : $x \\times 5$ s\'écrit $5x$',
        description: 'L\'élève ne connaît pas la convention d\'écriture du produit d\'un nombre par une lettre : le nombre s\'écrit devant, 3x et jamais x3.',
        notion: 'calcul littéral',
    },
    lettre_oubliee_en_remplacant: {
        libelle: 'Oublie la lettre en remplaçant : pour $t = 4$, $5t + 2 = 7$',
        regle: 'En remplaçant $x$ par un nombre, on garde l\'opération : pour $x = 2$, $3x = 3 \\times 2$ et $x + 8 = 2 + 8$',
        description: 'L\'élève remplace 5t par 5 au lieu de 5 × 4 : la lettre disparaît du calcul, 5t + 2 donne 7 pour t = 4.',
        notion: 'calcul littéral',
    },
    lettre_oubliee_x_plus_x: {
        libelle: 'Oublie la lettre : $x + x = 2$',
        regle: '$x + x = 2x$ mais $x \\times x = x^2$, le carré vient du produit',
        description: 'L\'élève compte les termes de $x + x$ et écrit 2 : la lettre disparaît.',
        notion: 'calcul littéral',
    },
    lit_la_figure_pas_le_codage: {
        libelle: 'Se fie au dessin, pas au codage : « (AB) // (DC) est indiqué »',
        regle: 'Seul le codage permet de conclure, pas l\'allure de la figure',
        description: 'L\'élève tient pour acquis ce que le dessin laisse voir, ou une propriété vraie mais non codée, alors que seul le codage fait foi.',
        notion: 'raisonnement géométrique',
    },
    losange_pris_pour_rectangle: {
        libelle: 'Confond losange et rectangle : « D\'après le codage, $ABCD$ est un rectangle »',
        regle: 'Parallélogramme à diagonales perpendiculaires ou à côtés consécutifs de même longueur : losange ; à diagonales de même longueur ou à un angle droit : rectangle',
        description: 'L\'élève prend un losange pour un rectangle et lui prête ce qui fait le rectangle : diagonales de même longueur, angle droit.',
        notion: 'parallélogrammes',
    },
    mauvais_cote_de_zero: {
        libelle: 'Ne relie pas le signe au côté de 0 : −4 compté vers la droite',
        regle: 'Les abscisses croissent vers la droite : négatifs à gauche de 0',
        description: 'L\'élève ne relie pas le signe moins à la gauche de l\'origine et place ou lit un nombre du côté de son opposé.',
        notion: 'nombres relatifs',
    },
    membre_droit_recopie: {
        libelle: 'Prend le nombre de droite pour la solution : 4x = 32 donne x = 32',
        regle: 'Dans $ax = c$, $x$ est multiplié par $a$, on divise : $x = c \\div a$, $8t = 48$ donne $t = 48 \\div 8 = 6$ ; dans $x + b = c$, on soustrait : $x = c - b$',
        description: 'L\'élève prend le nombre écrit après le signe égal pour la solution, sans voir que la solution est le nombre caché par la lettre.',
        notion: 'équations',
    },
    milieu_des_extremes: {
        libelle: 'Fait la moyenne des deux extrêmes : (plus petite + plus grande) ÷ 2',
        regle: 'On divise la somme des valeurs par le nombre de valeurs',
        description: 'L\'élève prend pour moyenne le milieu entre la plus petite et la plus grande valeur, sans tenir compte des autres valeurs.',
        notion: 'statistiques',
    },
    moins_a: {
        libelle: 'Lit 10 % comme « enlever 10 » : 10 % de 250 = 240',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève prend le pourcentage pour une quantité à retirer du nombre, sans le relier à une part sur 100.',
        notion: 'pourcentages',
    },
    moins_avale: {
        libelle: 'Oublie le signe entre parenthèses : 7 − (−5) = 2',
        regle: 'Soustraire un nombre, c\'est ajouter son opposé : $4 - (-6) = 4 + 6 = 10$ ; $11 - 12 = 11 + (-12) = -1$ ; $-8 - 4 = -8 + (-4) = -12$',
        description: 'L\'élève ne lit pas le signe du nombre entre parenthèses et calcule ou réécrit comme si ce nombre était positif.',
        notion: 'nombres relatifs',
    },
    moins_un_seul: {
        libelle: 'Ne retire du total qu\'un seul effectif : manquant = 54 − 5',
        regle: 'L\'effectif manquant, c\'est le total moins la somme des effectifs connus : $37 - (17 + 13) = 7$',
        description: 'L\'élève retranche du total un seul des effectifs connus, comme si les autres catégories ne comptaient pas.',
        notion: 'statistiques',
    },
    moitie_ecrite_a_l_envers: {
        libelle: 'Renverse la fraction : la moitié de $n$, c\'est $\\dfrac{2}{n}$',
        regle: 'La moitié de $n$, c\'est $n$ divisé par 2 : $\\dfrac{n}{2}$, le nombre au numérateur et 2 au dénominateur',
        description: 'L\'élève renverse la fraction : 2/n au lieu de n/2 pour la moitié de n.',
        notion: 'calcul littéral',
    },
    moitie_ecrite_moins_2: {
        libelle: 'Soustrait 2 au lieu de diviser par 2 : la moitié de $n$, c\'est $n - 2$',
        regle: 'La moitié de $n$, c\'est $n$ divisé par 2 : $\\dfrac{n}{2}$, le nombre au numérateur et 2 au dénominateur',
        description: 'L\'élève lit « divisé par 2 » comme « moins 2 » : n − 2 pour la moitié de n.',
        notion: 'calcul littéral',
    },
    mot_reciproque: {
        libelle: 'Confond un mot et son contraire : le double de 47 = 23,5',
        regle: 'La moitié de $n$, c\'est $\\dfrac{n}{2}$ et le double, $2n$ ; le successeur de $n$, c\'est $n + 1$ et le prédécesseur, $n - 1$',
        description: 'L\'élève connaît la paire de mots mais pas le sens de chacun, et applique l\'opération réciproque de celle demandée.',
        notion: 'calcul littéral',
    },
    moyenne_une_des_valeurs: {
        libelle: 'Prend une valeur de la série pour la moyenne : la plus fréquente',
        regle: 'La moyenne se calcule (la somme des valeurs divisée par leur nombre) : ce n\'est pas la valeur qui revient le plus souvent',
        description: 'L\'élève croit que la moyenne se choisit parmi les valeurs de la série, au lieu de la calculer en partageant le total également.',
        notion: 'statistiques',
    },
    multiplie_par_a: {
        libelle: 'Lit 100 % comme « ce nombre multiplié par 100 »',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève lit le pourcentage comme une multiplication par le nombre devant %, au lieu d\'une part sur 100 : 100 % d\'un nombre, c\'est ce nombre multiplié par 100.',
        notion: 'pourcentages',
    },
    nombre_de_facteurs_faux: {
        libelle: 'Compte un facteur de trop ou de moins : 10³ = 100',
        regle: '$a^n$ est le produit de $n$ facteurs égaux à $a$ : $4^3 = 4 \\times 4 \\times 4 = 64$',
        description: 'L\'élève sait qu\'une puissance est un produit de facteurs égaux mais relie mal l\'exposant au nombre de facteurs ou de zéros.',
        notion: 'puissances',
    },
    nombre_de_rangs_faux: {
        libelle: 'Décale d\'un rang de trop ou de moins',
        sousTitre: '$\\times$ ou $\\div$ par 10, 100, 1 000 : $263 \\div 10 = 2{,}63$',
        regle: 'Multiplier par 10, 100, 1 000 décale chaque chiffre de 1, 2, 3 rangs vers la gauche, diviser vers la droite : $263 \\div 10 = 26{,}3$ (1 rang)',
        description: 'L\'élève ne relie pas le nombre de zéros de 10, 100 ou 1 000 au nombre de rangs du décalage.',
        notion: 'nombres décimaux',
    },
    nombre_de_valeurs_faux: {
        libelle: 'Divise par un mauvais nombre de valeurs : 45 ÷ 4 pour 3 valeurs',
        regle: 'On divise la somme des valeurs par le nombre de valeurs',
        description: 'L\'élève divise le total par un nombre qui n\'est pas le nombre de valeurs : une valeur de trop, ou une valeur nulle oubliée dans le compte.',
        notion: 'statistiques',
    },
    nombre_ecrit_deux_fois: {
        libelle: 'Donne le nombre écrit deux fois : 7 × 3/7 = 7',
        regle: '$\\frac{a}{b}$ est le nombre qui, multiplié par $b$, donne $a$',
        description: 'L\'élève donne pour résultat le nombre qui apparaît deux fois, en facteur et au dénominateur, sans voir que 3/7 multiplié par 7 donne 3.',
        notion: 'fractions',
    },
    numerateur_seul: {
        libelle: 'Range les fractions comme leurs numérateurs : 11/18 > 5/6',
        description: 'L\'élève compare deux fractions comme deux entiers, en ne regardant que les numérateurs même si les dénominateurs diffèrent.',
        notion: 'fractions',
    },
    numerateurs_a_l_envers: {
        libelle: 'Range à l\'envers deux fractions de même dénominateur : $\\frac35 < \\frac25$',
        regle: 'À même dénominateur, la plus grande a le plus grand numérateur',
        description: 'L\'élève croit qu\'avec le même dénominateur, la fraction au plus grand numérateur est la plus petite, comme pour les dénominateurs.',
        notion: 'fractions',
    },
    operation_directe: {
        libelle: 'Refait l\'opération au lieu de la défaire : x + 5 = 12 donne x = 17',
        regle: 'Dans $ax = c$, $x$ est multiplié par $a$, on divise : $x = c \\div a$, $8t = 48$ donne $t = 48 \\div 8 = 6$ ; dans $x + b = c$, on soustrait : $x = c - b$',
        description: 'L\'élève applique au second membre l\'opération écrite dans l\'équation au lieu de l\'opération qui la défait.',
        notion: 'équations',
    },
    oppose_sans_changer_signe: {
        libelle: 'Garde le signe pour l\'opposé : l\'opposé de −7 est −7',
        regle: 'Deux opposés ont des signes contraires : $34$ et $-34$',
        description: 'L\'élève ne voit pas que prendre l\'opposé, c\'est changer le signe, et redonne le nombre lui-même.',
        notion: 'nombres relatifs',
    },
    opposes_consecutifs_confondus: {
        libelle: 'Confond opposés et consécutifs : « angles consécutifs égaux »',
        regle: 'Dans un parallélogramme, les côtés opposés et les angles opposés sont égaux ; les angles consécutifs sont supplémentaires ; côtés consécutifs égaux : losange',
        description: 'L\'élève ne distingue pas côtés ou angles opposés et côtés ou angles consécutifs, et prête aux uns la propriété des autres.',
        notion: 'parallélogrammes',
    },
    ordre_des_absolues_non_inverse: {
        libelle: 'Range les négatifs comme des positifs : −5 à droite de −2',
        regle: 'Les abscisses croissent vers la droite : négatifs à gauche de 0',
        description: 'L\'élève compare deux nombres négatifs par leurs distances à zéro, comme des positifs, sans voir que l\'ordre s\'inverse.',
        notion: 'nombres relatifs',
    },
    origine_obligatoire: {
        libelle: 'Croit le 0 indispensable : « sans 0 écrit, on ne peut rien placer »',
        regle: 'Partir d\'une graduation connue quand l\'origine n\'est pas écrite',
        description: 'L\'élève ne sait partir que de l\'origine et ignore qu\'une graduation dont l\'abscisse est connue sert aussi de point de départ.',
        notion: 'repérage',
    },
    parentheses_ignorees: {
        libelle: 'Calcule sans tenir compte des parenthèses : 48 − (14 + 20) = 54',
        regle: 'D\'abord les parenthèses, puis $\\times$ et $\\div$, puis $+$ et $-$',
        description: 'L\'élève ne voit pas que les parenthèses fixent l\'ordre du calcul et calcule de la même façon avec ou sans parenthèses.',
        notion: 'priorités opératoires',
    },
    part_seule: {
        libelle: 'Prend une seule part : 3/4 × 32 = 32 ÷ 4',
        regle: '$27 \\times \\frac{2}{3} = 27 \\div 3 \\times 2$',
        description: 'L\'élève partage par le dénominateur et s\'arrête, en prenant une part au lieu d\'autant de parts que le numérateur.',
        notion: 'fractions',
    },
    partie_decimale_recopiee: {
        libelle: 'Ne partage que la partie entière : la moitié de 4,5 = 2,5',
        regle: 'La moitié d\'un décimal, c\'est tout le nombre divisé par 2 : la moitié de 6,5, c\'est $6{,}5 \\div 2 = 3{,}25$, car la moitié de 0,5 est 0,25',
        description: 'L\'élève voit le décimal comme deux entiers séparés par la virgule : la partie entière est partagée, la partie décimale recopiée.',
        notion: 'nombres décimaux',
    },
    pas_converti: {
        libelle: 'Change le dénominateur, pas le numérateur : 3/4 − 1/5 = 2/20',
        regle: 'Pour changer de dénominateur, on multiplie le numérateur et le dénominateur par le même nombre : $\\frac{2}{5} = \\frac{2 \\times 4}{5 \\times 4} = \\frac{8}{20}$',
        description: 'L\'élève prend un dénominateur commun, ou garde l\'un de ceux de l\'énoncé, mais recopie les numérateurs sans les multiplier.',
        notion: 'fractions',
    },
    pas_pris_pour_1: {
        libelle: 'Croit qu\'une graduation vaut toujours 1, quel que soit le pas',
        regle: 'Trouver le pas de la graduation : il ne vaut pas toujours 1',
        description: 'L\'élève compte chaque graduation pour une unité, sans calculer le pas à partir de deux nombres écrits sur la droite.',
        notion: 'repérage',
    },
    pave_pris_pour_cube: {
        libelle: 'Nomme « cube » un pavé aux arêtes inégales',
        regle: 'Un cube est un pavé droit dont les 12 arêtes ont la même longueur et les 6 faces sont des carrés ; sinon, c\'est seulement un pavé droit',
        description: 'L\'élève reconnaît une boîte sans vérifier que toutes ses arêtes ont la même longueur, seule condition pour parler de cube.',
        notion: 'solides',
    },
    perimetre_de_base_pour_volume: {
        libelle: 'Multiplie le périmètre de la base par $h$ : $V = 2 \\times \\pi \\times r \\times h$',
        regle: 'Volume d\'un pavé, d\'un prisme, d\'un cylindre : aire de la base $\\times$ hauteur ; pavé $V = L \\times l \\times h$, cylindre $V = \\pi \\times r \\times r \\times h$',
        description: 'L\'élève multiplie le périmètre de la base par la hauteur au lieu de son aire : 2 × π × r × h pour un cylindre.',
        notion: 'volumes',
    },
    perimetre_du_disque_pour_aire: {
        libelle: 'Prend le périmètre du disque pour son aire : $A = 2 \\times \\pi \\times r$',
        regle: 'L\'aire du disque est $\\pi \\times$ rayon $\\times$ rayon : avec un diamètre, on le divise d\'abord par 2',
        description: 'L\'élève calcule le périmètre du disque, 2 × π × r, à la place de son aire, π × r × r.',
        notion: 'aires',
    },
    perimetre_pour_aire: {
        libelle: 'Prend le périmètre pour l\'aire : aire = 2 × (L + l)',
        regle: 'Distinguer l\'aire du périmètre : carré = côté $\\times$ côté',
        description: 'L\'élève calcule le tour de la figure quand on demande sa surface : 2 × (L + l) pour un rectangle, 4 × c pour un carré.',
        notion: 'aires',
    },
    petit_denominateur_gagne: {
        libelle: 'Croit que le plus petit dénominateur gagne toujours : 3/4 > 5/6',
        description: 'L\'élève applique la règle du même numérateur à des fractions de numérateurs différents : la fraction au plus petit dénominateur serait la plus grande.',
        notion: 'fractions',
    },
    pointe_ou_deux_bases: {
        libelle: 'Confond prisme et pyramide, cylindre et cône : prisme dit « pyramide »',
        regle: 'Reconnaître un prisme droit : deux bases reliées par des rectangles',
        description: 'L\'élève ne distingue pas un solide à deux bases superposables d\'un solide à une seule base et un sommet, dans un sens ou dans l\'autre.',
        notion: 'solides',
    },
    pourcent_lu_dixiemes: {
        libelle: 'Lit le symbole % comme « sur 10 » : 1 % de 400 = 40',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève relie le symbole % aux dixièmes et non aux centièmes, et se trompe d\'un rang en calculant ou en convertissant.',
        notion: 'pourcentages',
    },
    pourcent_lu_dixiemes_frequence: {
        libelle: 'Confond dixièmes et centièmes : « 0,6 et 6 % désignent la même fréquence »',
        regle: 'Une même fréquence s\'écrit sous forme fractionnaire, décimale ou de pourcentage, $a$ % étant $a$ centièmes : $\\frac{6}{20} = 0{,}30 = 30$ %',
        description: 'L\'élève confond dixièmes et centièmes entre écriture décimale et pourcentage d\'une fréquence : 0,6 pris pour 6 %, 1,6 pour 16 %.',
        notion: 'statistiques',
    },
    pourcentage_voisin: {
        libelle: 'Confond deux pourcentages repères : 10 % de 440 = 110',
        regle: '$a$ % de $c$, c\'est $\\frac{a}{100} \\times c$ : 100 % = $c$, 50 % = $c \\div 2$, 25 % = $c \\div 4$, 10 % = $c \\div 10$, 1 % = $c \\div 100$',
        description: 'L\'élève n\'a pas fixé quelle part du nombre va avec chaque pourcentage repère et prend celle d\'un autre repère de la suite 100, 50, 25, 10, 1.',
        notion: 'pourcentages',
    },
    presque_carre: {
        libelle: 'Prend un nombre proche d\'un carré pour un carré parfait : 120',
        regle: 'Le carré d\'un nombre, c\'est ce nombre multiplié par lui-même ; connaitre les carrés parfaits de 0 à 12 : $64 = 8 \\times 8 = 8^2$, $144 = 12^2$',
        description: 'L\'élève se fie à l\'allure du nombre et ne cherche pas l\'entier qui, multiplié par lui-même, le donne.',
        notion: 'puissances',
    },
    produit_confondu_avec_puissance: {
        libelle: 'Prend $11 + 11 + 11$ pour $11 \\times 11 \\times 11$',
        regle: 'La multiplication est une addition itérée : $11 + 11 + 11 = 3 \\times 11$ (3 termes égaux à 11)',
        description: 'L\'élève confond l\'addition itérée (3 × 11, trois fois 11) et le produit de trois facteurs égaux à 11, dans un sens ou dans l\'autre.',
        notion: 'multiplication',
    },
    produit_des_nombres_vus: {
        libelle: 'Multiplie dès qu\'un × apparaît : pour 3 × … = 12, calcule 3 × 12',
        regle: 'Dans $a \\times \\ldots = b$, le nombre manquant est $b \\div a$, le produit divisé par le facteur connu : $5 \\times \\ldots = 3$ donne $3 \\div 5$',
        description: 'L\'élève multiplie les nombres en vue dès qu\'un × apparaît, sans voir que le facteur manquant se trouve par une division.',
        notion: 'fractions',
    },
    produit_ecrit_en_somme: {
        libelle: 'Ajoute le nombre de termes au terme : $11 + 11 + 11 = 3 + 11$',
        regle: 'La multiplication est une addition itérée : $11 + 11 + 11 = 3 \\times 11$ (3 termes égaux à 11)',
        description: 'L\'élève remplace la multiplication de l\'addition itérée par une addition : 11 + 11 + 11 devient 3 + 11.',
        notion: 'multiplication',
    },
    quart_de_tour_pour_demi_tour: {
        libelle: 'Fait un quart de tour au lieu d\'un demi-tour : M, O, M\' pas alignés',
        regle: 'Le symétrique $M\'$ de $M$ par le demi-tour de centre $O$ : $O$ est le milieu de $[MM\']$, donc $M$, $O$ et $M\'$ sont alignés et $OM\' = OM$',
        description: 'L\'élève fait tourner le point d\'un quart de tour autour du centre au lieu d\'un demi-tour : le point, le centre et l\'image ne sont pas alignés.',
        notion: 'transformations',
    },
    quotient_a_l_envers: {
        libelle: 'Divise dans le mauvais sens : $9 \\times \\ldots = 19$ donne $9 \\div 19$',
        regle: 'Dans $a \\times \\ldots = b$, le nombre manquant est $b \\div a$, le produit divisé par le facteur connu : $5 \\times \\ldots = 3$ donne $3 \\div 5$',
        description: 'L\'élève divise le facteur connu par le produit, au lieu du produit par le facteur connu : 9 × … = 19 donne 9 ÷ 19.',
        notion: 'multiplication',
    },
    racine_de_racine: {
        libelle: 'Prend la racine une fois de trop : 81 est le carré de 3 (et non 9)',
        description: 'L\'élève prend la racine de la racine : sachant 81 = 9 × 9 et 9 = 3 × 3, l\'élève répond 3 au lieu de 9.',
        notion: 'puissances',
    },
    racine_lue_comme_moitie: {
        libelle: 'Divise par 2, comme si le carré était le double : 64 est le carré de 32',
        regle: 'Le carré d\'un nombre, c\'est ce nombre multiplié par lui-même ; connaitre les carrés parfaits de 0 à 12 : $64 = 8 \\times 8 = 8^2$, $144 = 12^2$',
        description: 'L\'élève lit « multiplié par lui-même » comme « multiplié par 2 » et divise par 2 pour retrouver le nombre.',
        notion: 'puissances',
    },
    rangs_faux_en_completant: {
        libelle: 'Compte un rang de trop ou de moins : $8 = 0{,}08 \\times 1000$',
        regle: 'Multiplier par 10, 100, 1 000 décale chaque chiffre de 1, 2, 3 rangs : de 1,2 à 1 200, 3 rangs, donc $1\\,200 = 1{,}2 \\times 1\\,000$',
        description: 'L\'élève compte mal le décalage des chiffres pour trouver 10, 100 ou 1 000 : un rang de trop ou de moins, 8 = 0,08 × 1000.',
        notion: 'nombres décimaux',
    },
    rectangle_losange_confondus: {
        libelle: 'Échange rectangle et losange : diagonales égales, donc losange',
        regle: 'Un parallélogramme à diagonales de même longueur est un rectangle',
        description: 'L\'élève prend un rectangle pour un losange et lui prête ce qui fait le losange : diagonales perpendiculaires, côtés consécutifs égaux.',
        notion: 'parallélogrammes',
    },
    refus_du_codage: {
        libelle: 'Doute du codage : « il faudrait aussi mesurer pour être sûr »',
        regle: 'Le codage d\'une propriété caractéristique suffit pour conclure, sans mesurer : des diagonales qui se coupent en leur milieu, c\'est un parallélogramme',
        description: 'L\'élève ne tient pas le codage pour une information sûre et attend une mesure ou une marque de plus avant d\'en tirer une conclusion.',
        notion: 'raisonnement géométrique',
    },
    reste_oublie: {
        libelle: 'Arrête la division à l\'entier : 50 % de 9 = 4',
        description: 'L\'élève reste dans les nombres entiers et arrête la division au quotient entier, en perdant la demie ou les dixièmes.',
        notion: 'pourcentages',
    },
    routine_appliquee: {
        libelle: 'Décale la virgule même pour × 1 ou ÷ 1 : 9,4 × 1 = 94',
        regle: 'Multiplier ou diviser par 1 ne change pas le nombre : $7{,}25 \\times 1 = 7{,}25$ ; seuls 10, 100, 1 000 décalent les chiffres de 1, 2, 3 rangs',
        description: 'L\'élève décale la virgule par réflexe, sans vérifier que multiplier ou diviser par 1 ne change pas le nombre.',
        notion: 'nombres décimaux',
    },
    sens_du_glissement_inverse: {
        libelle: 'Décale la virgule du mauvais côté : 3 × 100 = 0,03 et 3 ÷ 100 = 300',
        regle: 'Multiplier par 10, 100, 1 000 décale chaque chiffre de 1, 2, 3 rangs vers la gauche, diviser vers la droite : $263 \\div 10 = 26{,}3$ (1 rang)',
        description: 'L\'élève ne relie pas le sens du décalage à l\'opération et rend le nombre plus petit quand on multiplie par 10, 100 ou 1 000, plus grand quand on divise.',
        notion: 'nombres décimaux',
    },
    signe_de_coordonnee_a_l_envers: {
        libelle: 'Lit une coordonnée avec le mauvais signe : $B(2 ; 2)$ pour $B(2 ; -2)$',
        regle: 'Les coordonnées d\'un point : l\'abscisse d\'abord, négative à gauche de l\'origine ; l\'ordonnée ensuite, négative en dessous de l\'origine : $B(2 ; -2)$',
        description: 'L\'élève lit la bonne distance sur un axe mais se trompe de signe pour l\'une des deux coordonnées : B(2 ; 2) pour B(2 ; −2).',
        notion: 'repérage',
    },
    signe_inverse: {
        libelle: 'Trouve la bonne distance, pas le bon signe : 2 − 13 = 11',
        regle: 'Soustraire un nombre, c\'est ajouter son opposé : $4 - (-6) = 4 + 6 = 10$ ; $11 - 12 = 11 + (-12) = -1$ ; $-8 - 4 = -8 + (-4) = -12$',
        description: 'L\'élève trouve la bonne distance à zéro mais ne sait pas que le résultat prend le signe du nombre le plus éloigné de zéro.',
        notion: 'nombres relatifs',
    },
    signe_inverse_addition: {
        libelle: 'Garde le signe du plus proche de zéro : $2 + (-14) = 12$',
        regle: 'À signes contraires, on soustrait les distances à zéro et on garde le signe du plus loin de zéro : $2 + (-14) = -12$',
        description: 'L\'élève soustrait bien les distances à zéro mais garde le signe du nombre le plus proche de zéro.',
        notion: 'nombres relatifs',
    },
    signe_lu_a_l_envers: {
        libelle: 'Lit l\'abscisse avec le mauvais signe : $-2$ pour $+2$',
        regle: 'Sur une droite graduée, un point à droite de l\'origine a une abscisse positive, à gauche, négative : 2 unités à droite de 0, c\'est $+2$',
        description: 'L\'élève lit la bonne distance à l\'origine mais se trompe de signe : −2 pour +2 à droite de 0, +1 pour −1 à gauche.',
        notion: 'repérage',
    },
    signes_ignores: {
        libelle: 'Additionne sans les signes : −6 + 11 = 17',
        regle: 'À signes contraires, on soustrait les distances à zéro et on garde le signe du plus loin de zéro : $2 + (-14) = -12$',
        description: 'L\'élève ajoute les distances à zéro des nombres sans tenir compte de leurs signes, ni dans le calcul ni dans le résultat.',
        notion: 'nombres relatifs',
    },
    signes_ignores_deux_negatifs: {
        libelle: 'Donne un résultat positif à la somme de deux négatifs : $(-8) + (-7) = 15$',
        regle: 'Pour ajouter deux nombres de même signe, on ajoute les distances à zéro et on garde ce signe : $(-8) + (-7) = -15$',
        description: 'L\'élève donne un résultat positif à la somme de deux négatifs : les distances à zéro sont ajoutées, le signe moins n\'est pas gardé.',
        notion: 'nombres relatifs',
    },
    signes_ignores_soustraction: {
        libelle: 'Additionne les distances à zéro sans les signes : $11 - 12 = 23$',
        regle: 'Soustraire un nombre, c\'est ajouter son opposé : $4 - (-6) = 4 + 6 = 10$ ; $11 - 12 = 11 + (-12) = -1$ ; $-8 - 4 = -8 + (-4) = -12$',
        description: 'L\'élève ajoute les distances à zéro sans tenir compte des signes : 11 − 12 = 23, (−14) − (−6) = 20.',
        notion: 'nombres relatifs',
    },
    somme_connus: {
        libelle: 'Oublie de retrancher du total : effectif manquant = 17 + 9',
        regle: 'L\'effectif manquant, c\'est le total moins la somme des effectifs connus : $37 - (17 + 13) = 7$',
        description: 'L\'élève additionne les effectifs connus et s\'arrête là, sans voir que l\'effectif manquant est ce qui reste du total.',
        notion: 'statistiques',
    },
    somme_des_opposes_doublee: {
        libelle: 'Double au lieu d\'annuler : $-21 + 21 = -42$',
        regle: 'Deux opposés ont la même distance à zéro et des signes contraires : leur somme vaut 0, $-21 + 21 = 0$',
        description: 'L\'élève ajoute les distances à zéro de deux opposés et garde le signe moins, au lieu de trouver 0.',
        notion: 'nombres relatifs',
    },
    somme_produit_confondus: {
        libelle: 'Multiplie dans une somme, additionne dans un produit : 2x + 5x = 10x',
        regle: '$3x + 2x = 5x$ et $3x \\times 2x = 6x^2$, nombres devant $x$ compris',
        description: 'L\'élève échange les gestes de la somme et du produit sur les nombres placés devant les lettres.',
        notion: 'calcul littéral',
    },
    substitution_en_puissance: {
        libelle: 'Lit le nombre devant la lettre comme un exposant : $2x$, pour $x = 6$, c\'est 36',
        regle: 'En remplaçant $x$ par un nombre, on garde l\'opération : pour $x = 2$, $3x = 3 \\times 2$ et $x + 8 = 2 + 8$',
        description: 'L\'élève remplace la lettre mais lit le nombre devant elle comme un exposant : 2x pour x = 6 donne 6 puissance 2, soit 36.',
        notion: 'calcul littéral',
    },
    substitution_en_somme: {
        libelle: 'Ajoute au lieu de multiplier en remplaçant : $2x$, pour $x = 6$, c\'est 8',
        regle: 'En remplaçant $x$ par un nombre, on garde l\'opération : pour $x = 2$, $3x = 3 \\times 2$ et $x + 8 = 2 + 8$',
        description: 'L\'élève remplace la lettre mais ajoute le nombre devant elle au lieu de le multiplier : 2x pour x = 6 donne 8.',
        notion: 'calcul littéral',
    },
    successeur_ecrit_en_double: {
        libelle: 'Confond le successeur et le double : le successeur de $n$, c\'est $2n$',
        regle: 'Le successeur de $n$, c\'est le nombre entier qui suit : $n + 1$ ; le double de $n$, c\'est $2n$',
        description: 'L\'élève écrit le successeur d\'un nombre comme son double : 2n au lieu de n + 1.',
        notion: 'calcul littéral',
    },
    terme_confondu: {
        libelle: 'Met le nombre de termes à la place du terme répété : 5 + 5 + 5 = 3 × 3',
        regle: 'La multiplication est une addition itérée : $11 + 11 + 11 = 3 \\times 11$ (3 termes égaux à 11)',
        description: 'L\'élève sait qu\'une somme répétée s\'écrit en produit mais perd de vue le nombre qui se répète et met le nombre de termes à sa place.',
        notion: 'multiplication',
    },
    termes_mal_comptes: {
        libelle: 'Compte un terme de trop ou de moins : 5 + 5 + 5 = 4 × 5',
        regle: 'La multiplication est une addition itérée : $11 + 11 + 11 = 3 \\times 11$ (3 termes égaux à 11)',
        description: 'L\'élève écrit en produit une somme de termes égaux mais se trompe d\'un dans le compte des termes.',
        notion: 'multiplication',
    },
    termes_pas_tous_egaux: {
        libelle: 'Écrit en produit des termes pas tous égaux : 5 + 5 + 6 = 3 × 5',
        regle: 'La multiplication est une addition itérée : $11 + 11 + 11 = 3 \\times 11$ (3 termes égaux à 11)',
        description: 'L\'élève écrit en produit une somme dont un terme diffère des autres, sans vérifier que tous les termes sont égaux.',
        notion: 'multiplication',
    },
    tiers_abusif: {
        libelle: 'Divise par 3 le volume d\'un prisme ou d\'un cylindre',
        regle: 'Le $\\div 3$ ne vaut que pour la pyramide et le cône ; le prisme et le cylindre ont deux bases : $V$ = aire de la base $\\times$ hauteur',
        description: 'L\'élève applique le ÷ 3 de la pyramide et du cône à un solide à deux bases, sans relier ce tiers à la forme en pointe.',
        notion: 'volumes',
    },
    total_pas_divise: {
        libelle: 'Donne le total sans le partager : moyenne de 12 ; 15 ; 18 = 45',
        regle: 'On divise la somme des valeurs par le nombre de valeurs',
        description: 'L\'élève additionne les valeurs et s\'arrête : le total est donné pour la moyenne, sans être divisé par le nombre de valeurs.',
        notion: 'statistiques',
    },
    total_pris_pour_le_maximum: {
        libelle: 'Prend le plus grand effectif pour le total : 5, 8 et 7, total 8',
        description: 'L\'élève lit le total comme un effectif de plus, le plus grand du tableau, et non comme la somme de tous les effectifs.',
        notion: 'statistiques',
    },
    traits_au_lieu_d_intervalles: {
        libelle: 'Compte les traits, pas les intervalles : de 0 à 0,2, 3 traits, lit 0,3',
        regle: 'On compte les intervalles (le nombre de pas), jamais les traits : avec un pas de $0{,}1$, 4 pas depuis 0, c\'est $0{,}4$',
        description: 'L\'élève compte les traits au lieu des intervalles qui les séparent et tombe sur la graduation voisine, une de trop ou une de moins.',
        notion: 'repérage',
    },
    translation_pour_axiale: {
        libelle: 'Confond symétrie axiale et translation : « F a seulement glissé »',
        regle: 'Par une symétrie axiale, la figure est retournée comme dans un miroir (pliage le long de l\'axe) ; si elle a seulement glissé, c\'est une translation',
        description: 'L\'élève ne regarde pas si la figure est retournée et prend une symétrie axiale pour un simple glissement, ou un glissement pour une symétrie axiale.',
        notion: 'transformations',
    },
    triangle_particulier_ignore: {
        libelle: 'Croit distinctes des droites confondues, en triangle isocèle ou équilatéral',
        regle: 'Triangle isocèle : la hauteur issue du sommet principal est aussi médiane, bissectrice et médiatrice du côté opposé. Équilatéral : vrai en chaque sommet',
        description: 'L\'élève garde l\'image du triangle quelconque et ne voit pas que, dans un triangle isocèle ou équilatéral, une même droite joue plusieurs rôles.',
        notion: 'droites remarquables',
    },
    triangle_sans_le_demi: {
        libelle: 'Oublie le ÷ 2 du triangle : aire = base × hauteur',
        regle: 'L\'aire du triangle est base $\\times$ hauteur $\\div 2$',
        description: 'L\'élève multiplie la base par la hauteur sans diviser par 2, comme pour un rectangle, pour l\'aire d\'un triangle ou la base d\'un prisme.',
        notion: 'aires',
    },
    trou_par_soustraction: {
        libelle: 'Traite 3 × … = 27 comme 3 + … = 27 : calcule 27 − 3',
        regle: 'Dans $a \\times \\ldots = b$, le nombre manquant est $b \\div a$, le produit divisé par le facteur connu : $5 \\times \\ldots = 3$ donne $3 \\div 5$',
        description: 'L\'élève voit dans le trou « ce qui manque » et le cherche par une soustraction, comme dans une addition à trou.',
        notion: 'multiplication',
    },
    un_seul_point_transforme: {
        libelle: 'Ne transforme qu\'un point : « l\'image garde une extrémité »',
        regle: 'Chaque point de la figure se transforme : l\'image de $[AB]$ est $[A\'B\']$ ; une extrémité ne reste en place que si elle est sur l\'axe ou au centre',
        description: 'L\'élève croit que l\'image reste accrochée à la figure de départ par un point, au lieu de transformer chacun de ses points.',
        notion: 'transformations',
    },
    un_seul_terme: {
        libelle: 'Ne garde que l\'opération prioritaire : 8 + 10 ÷ 2 = 5',
        regle: 'D\'abord les parenthèses, puis $\\times$ et $\\div$, puis $+$ et $-$',
        description: 'L\'élève fait l\'opération prioritaire puis s\'arrête, comme si la priorité voulait dire ne faire que celle-là.',
        notion: 'priorités opératoires',
    },
    unite_confondue: {
        libelle: 'Confond unités de longueur, d\'aire et de volume : aire en cm',
        regle: 'L\'unité dépend de ce qu\'on mesure : une longueur en cm, une aire en cm² (carré de 1 cm de côté), un volume en cm³ (cube de 1 cm d\'arête)',
        description: 'L\'élève choisit l\'unité sans la relier à ce qui est mesuré, alors qu\'une longueur, une aire et un volume ont chacun la leur.',
        notion: 'aires',
    },
    x_garde_seul: {
        libelle: 'Garde $x$ seul : $x + x = x$, $x \\times x = x$',
        regle: '$x + x = 2x$ mais $x \\times x = x^2$, le carré vient du produit',
        description: 'L\'élève garde $x$ seul pour $x + x$ ou $x \\times x$, comme si la lettre absorbait l\'opération ($1 \\times x = x$).',
        notion: 'calcul littéral',
    },
    x_oublie: {
        libelle: 'Oublie la lettre dans le calcul : 2t × 6t = 12t',
        regle: '$3x + 2x = 5x$ et $3x \\times 2x = 6x^2$, nombres devant $x$ compris',
        description: 'L\'élève calcule avec les seuls nombres écrits, puis recopie la lettre ou la perd, sans la traiter comme un nombre.',
        notion: 'calcul littéral',
    },
    zero_confondu: {
        libelle: 'Donne 0 pour l\'opposé : l\'opposé de 48 est 0',
        regle: 'Deux opposés ont des signes contraires : $34$ et $-34$',
        description: 'L\'élève retient que l\'opposé « donne 0 » et confond l\'opposé d\'un nombre avec la somme de ce nombre et de son opposé.',
        notion: 'nombres relatifs',
    },
};

/** Le libellé d'un code (le code lui-même s'il n'est pas au catalogue). */
export const libelleErreur = (code) => ERREURS[code]?.libelle ?? code;
